/**
 * Route guard.
 *
 * Behavior:
 *   - Not authenticated → <Navigate to="/login" />, preserving the current
 *     location in `state` so a post-login redirect can send the user back.
 *   - Authenticated but not hydrated yet → a small loading splash.
 *   - Authenticated & hydrated → render `<Outlet />`.
 *
 * If the bearer token expires mid-session, the axios interceptor handles the
 * refresh dance transparently; only if *that* fails will it clear tokens and
 * hard-redirect to /login.
 */

import { useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';

import { useAuthStore } from '@/stores/auth';

export function ProtectedRoute() {
  const location = useLocation();
  const accessToken = useAuthStore((s) => s.accessToken);
  const user = useAuthStore((s) => s.user);
  const hydrated = useAuthStore((s) => s.hydrated);
  const hydrate = useAuthStore((s) => s.hydrate);

  useEffect(() => {
    if (!hydrated) {
      void hydrate();
    }
  }, [hydrated, hydrate]);

  if (!accessToken) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (!hydrated || !user) {
    return (
      <div
        role="status"
        aria-live="polite"
        className="flex min-h-screen items-center justify-center bg-background"
      >
        <Loader2 className="size-8 animate-spin text-muted-foreground" />
        <span className="sr-only">Carregando...</span>
      </div>
    );
  }

  return <Outlet />;
}
